import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { LineChart, ExternalLink, RefreshCw, LogOut, CheckCircle2, Clock } from "lucide-react";

export default function Paywall() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [billing, setBilling] = useState(null);
  const [requesting, setRequesting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/billing/status");
      setBilling(data);
      if (data.has_access) nav("/app");
    } catch {/* ignore */}
  };
  useEffect(() => { load(); const i = setInterval(load, 8000); return () => clearInterval(i); }, []);

  const requestAccess = async () => {
    setRequesting(true);
    try {
      await api.post("/billing/request-access");
      toast.success("Payment confirmation received. Admin will activate your account shortly.");
      await refresh();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setRequesting(false); }
  };

  const handleLogout = async () => { await logout(); nav("/login"); };

  const status = billing?.status || user?.subscription_status || "none";
  const isPending = status === "pending";

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100">
      <header className="border-b border-white/5">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LineChart className="w-5 h-5 text-teal-400" strokeWidth={1.5} />
            <div className="font-mono text-sm tracking-widest uppercase">BonBot</div>
          </div>
          <button data-testid="paywall-logout" onClick={handleLogout}
                  className="text-zinc-500 hover:text-zinc-100 flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest">
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-5 py-12 sm:py-20" data-testid="paywall-page">
        <div className="text-[10px] uppercase tracking-[0.25em] text-teal-400 mb-4 font-mono">
          // One last step
        </div>
        <h1 className="text-4xl sm:text-5xl font-medium tracking-tight leading-[1.05]">
          Activate your <span className="text-teal-400">BonBot</span> access.
        </h1>
        <p className="text-zinc-400 mt-5 max-w-xl leading-relaxed">
          A one-time payment of <span className="text-zinc-100 font-medium">{billing?.price_display || "$99.99"}</span> unlocks
          the trading engine, broker integration, and your personal admin-monitored dashboard.
        </p>

        {/* Status card */}
        <div className="mt-10 border border-white/10 bg-[#121214] rounded-sm p-6">
          {isPending ? (
            <div className="flex flex-col sm:flex-row items-start gap-4" data-testid="paywall-pending-state">
              <div className="w-12 h-12 rounded-sm bg-amber-400/10 border border-amber-400/20 flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5 text-amber-400" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <div className="text-lg font-medium tracking-tight">Awaiting admin review</div>
                <p className="text-zinc-500 text-sm mt-1.5 leading-relaxed">
                  We've flagged your payment for review. Once the admin confirms your Square transaction,
                  your account unlocks automatically. This page refreshes every 8 seconds.
                </p>
                <button data-testid="paywall-refresh-button" onClick={load}
                        className="mt-4 flex items-center gap-2 border border-white/10 hover:bg-white/5 px-3 py-1.5 text-xs font-mono uppercase tracking-widest rounded-sm">
                  <RefreshCw className="w-3 h-3" /> Check status now
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              <ol className="space-y-4 text-sm font-mono">
                <li className="flex gap-3">
                  <span className="text-teal-400 shrink-0">01</span>
                  <div>
                    <div className="text-zinc-100">Pay via Square (secure hosted checkout)</div>
                    <a data-testid="paywall-square-link"
                       href={billing?.payment_url || "https://square.link/u/EZ0DSvTD"}
                       target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-2 mt-3 bg-teal-400 text-[#09090B] hover:bg-teal-300 px-5 py-2.5 text-xs uppercase tracking-widest rounded-sm font-medium">
                      Pay {billing?.price_display || "$99.99"} <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="text-teal-400 shrink-0">02</span>
                  <div>
                    <div className="text-zinc-100">Come back and tap below — admin reviews & activates within 24h</div>
                    <button data-testid="paywall-paid-button"
                            disabled={requesting}
                            onClick={requestAccess}
                            className="mt-3 border border-teal-400/30 text-teal-400 hover:bg-teal-400/10 px-5 py-2.5 text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-50">
                      {requesting ? "Sending…" : "I've paid — request access"}
                    </button>
                  </div>
                </li>
              </ol>
            </div>
          )}
        </div>

        <div className="mt-8 text-xs text-zinc-600 font-mono space-y-1">
          <div>// Account: {user?.email}</div>
          <div>// Status: <span className={isPending ? "text-amber-400" : "text-zinc-400"}>{status}</span></div>
          <div>// Questions? <a className="text-teal-400 hover:underline" href="mailto:support@bonbot.biz">support@bonbot.biz</a></div>
        </div>
      </div>
    </div>
  );
}
